/**
 * モーションシーンの BGM 同期の鮮度 (docs/features/brand-cut-motion-graphics-plan.md §10.4 / §15.4・Phase 2d)。
 *
 * 判定そのものは backend (motion_scene.scenario_motion_sync_status) が正本。ここは「いつ取り直すか」だけを決める純関数。
 */
import { isMotionScene } from './productionMode';
import { isMotionV2Script } from './scriptContract';

interface MotionSyncSceneSource {
  id?: string | null;
  order?: number | null;
  video_input_mode?: string | null;
  video_material_id?: string | null;
  video_duration_seconds?: number | null;
  video_excluded?: boolean | null;
  narration_duration_seconds?: number | null;
  narration_material_id?: string | null;
  narration_excluded?: boolean | null;
  narration?: string | null;
  dialogue?: string | null;
  script?: string | null;
  dialogue_lines?: unknown;
  brand_cut_position?: string | null;
  generation_metadata?: Record<string, unknown> | null;
}

interface MotionSyncScenarioSource {
  id?: string | null;
  bgm_material_id?: string | null;
  bgm_excluded?: boolean | null;
  generation_metadata?: Record<string, unknown> | null;
  scenes?: MotionSyncSceneSource[] | null;
}

/** 鮮度判定の対象 = 制作方式がモーションで、モーション経路 (engine='motion') で作った動画が付いているシーン (BE と同一規則)。 */
export function isGeneratedMotionScene(scene: MotionSyncSceneSource): boolean {
  return isMotionScene(scene)
    && !!scene.video_material_id
    && scene.generation_metadata?.engine === 'motion';
}

/** ナレーション欄の読み上げ本文 (backend resolve_narration_text / narrationUtils.resolveNarrationText と同じ規則)。 */
function narrationTextOf(scene: MotionSyncSceneSource): string {
  if (scene.narration) return scene.narration;
  if (scene.dialogue) return '';
  return scene.script || '';
}

/**
 * 同期の鮮度が変わり得る入力の要約。これが変わったときだけ取り直す (ポーリングはしない)。
 * 生成済みのモーションシーンが 1 つも無ければ null (取得しない)。
 * 含めるもの: BGM (差し替え / 除外)・各シーンの id / 順序 / 尺 / 動画 / ナレーション尺 / ブランドカット位置
 * (開始秒は先行シーンの尺の和。歌モノは冒頭ブランドカットの後から BGM が鳴る)。
 * 声の鮮度 (sync-status の voice) は旧契約のモーションでも返る (ルートが scenario_voice_input_status を合成する) ので、
 * 声の素材 / 除外・ナレーション本文・セリフ行・声の対応表は台本の契約によらず含める (素材 ID と尺が同じまま本文だけ
 * 変わる「セリフとナレーションの入れ替え」でも取り直すため。docs/features/scenario-narration-dialogue-swap-plan.md §3.9)。
 */
export function motionSyncSignature(scenario: MotionSyncScenarioSource): string | null {
  const scenes = scenario.scenes || [];
  // 声の台本 (motion-v2・§10.7.3) は映像が未生成でも声の鮮度 (voice) を出すので、シーンがあれば取得する
  const voiceScript = isMotionV2Script(scenario);
  if (!voiceScript && !scenes.some(isGeneratedMotionScene)) return null;
  if (voiceScript && scenes.length === 0) return null;
  const sceneParts = [...scenes]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((s) => [
      s.id ?? '',
      s.order ?? '',
      s.video_duration_seconds ?? '',
      s.video_material_id ?? '',
      s.video_excluded ? 1 : 0,
      s.narration_duration_seconds ?? '',
      s.brand_cut_position ?? '',
      s.video_input_mode ?? '',
      s.narration_material_id ?? '',
      s.narration_excluded ? 1 : 0,
      JSON.stringify(narrationTextOf(s)),
      JSON.stringify(s.dialogue_lines ?? null),
    ].join(':'));
  const voiceMap = JSON.stringify(scenario.generation_metadata?.voice_map ?? null);
  return [scenario.bgm_material_id ?? '', scenario.bgm_excluded ? 1 : 0, voiceMap, ...sceneParts].join('|');
}
